import React, {
  useCallback,
  useRef,
} from 'react';

import { roundToTwoDecimals } from '@consumer-frontend/nc-utils';

import { NcCustomAmountLabel } from './NcCustomAmountLabel';
import { NcInput, NcInputPropsT } from '../NcInput';
import { NcModalAnchor, NcModalAnchorPropsT } from '../NcModal/NcModalAnchor';
import { NcRadioButton } from '../NcRadioButton';

export type NcCustomAmountPropsT = {
  errorMessage: React.ReactNode;
  isChecked: boolean;
  onSave: (amount: number) => void;
  onCancel?: () => void;
  value?: number;
};

export type NcCustomAmountT = React.FC<NcCustomAmountPropsT>;

export const NcCustomAmount: NcCustomAmountT = ({
  isChecked,
  errorMessage,
  onCancel,
  onSave,
  value,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSave = useCallback(
    () => {
      const parsedAmount = parseFloat(inputRef.current?.value ?? '');

      if (parsedAmount && !Number.isNaN(parsedAmount)) { onSave(roundToTwoDecimals(parsedAmount)); }
    },
    [inputRef, onSave],
  );

  const handleCancel = useCallback(() => { onCancel?.(); }, [onCancel]);

  const inputProps: NcInputPropsT = {
    className: 'nc-custom-amount-input',
    defaultValue: value,
    id: 'custom-amount-input',
    label: 'Custom amount',
    name: 'custom-amount',
    prefix: '$',
    type: 'number',
    value: inputRef.current?.value,
  };

  const modalAnchorProps: Omit<NcModalAnchorPropsT, 'children'> = {
    body: <NcInput {...inputProps} ref={inputRef} />,
    modalOnClick: handleSave,
    modalOnSecondaryClick: handleCancel,
    primaryText: 'Save',
    secondaryText: 'Cancel',
  };

  return (
    <div className="nc-custom-amount">
      <NcModalAnchor {...modalAnchorProps}>
        <NcRadioButton
          checked={!!value && isChecked}
          errorMessage={errorMessage}
          groupError
          id="payment-amount--custom"
          label={(<NcCustomAmountLabel amount={isChecked ? value : undefined} />)}
          // The following noop onChange handler is necessary because it tells React this is a controlled component.
          // Without it, React throws a warning because the checked prop is defined but there is no onChange handler.
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onChange={() => {}}
          value={value}
        />
      </NcModalAnchor>
    </div>
  );
};
